import { ChangeDetectionStrategy, Component, DestroyRef, effect, inject, INJECTOR, linkedSignal, OnInit, resource, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { LinkTreeModule, NavItem, NOOP_CLICK_HANDLER } from '@easihub/ngx';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { SidebarService } from './sidebar.service';

@Component({
  selector: 'app-primary-sidebar',
  templateUrl: './primary-sidebar.component.html',
  styleUrl: './primary-sidebar.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    LinkTreeModule,
    MatSidenavModule,
    FontAwesomeModule,
  ]
})
export class AppPrimarySidebarComponent implements OnInit {
  private readonly injector = inject(INJECTOR);
  private readonly dRef = inject(DestroyRef);
  private readonly service = inject(SidebarService);
  private readonly outer = inject(MatSidenav, { host: true });

  protected readonly icons = {
    faArrowLeft,
  } as const;

  protected readonly items = (() => {
    const inner = resource({
      loader: async () => [
      ],
      defaultValue: []
    });

    const outer = [
      {
        label: 'Home',
        route: '/'
      },
      {
        label: 'Community',
        type: 'section',
        children: [
          {
            label: 'Community Guidelines',
            route: '/community/guidelines'
          },
          {
            label: 'Posting Guidelines',
            route: '/community/posting-guidelines'
          },
          {
            label: 'Code of Conduct',
            route: '/community/code-of-conduct'
          },
          {
            label: 'Join as Moderator',
            route: '/community/join-moderator'
          },
          {
            label: 'Getting Started Guide',
            route: '/community/getting-started'
          },
          {
            label: 'Browse All Topics',
            href: 'https://community.easihub.com'
          },
          {
            label: 'ERP Systems',
            type: 'group',
            children: [
              {
                label: 'SAP ERP & S/4HANA',
                click: NOOP_CLICK_HANDLER,
              },
              {
                label: 'Oracle ERP Cloud',
                click: NOOP_CLICK_HANDLER,
              },
              {
                label: 'Microsoft Dynamics 365',
                click: NOOP_CLICK_HANDLER,
              },
              {
                label: 'NetSuite ERP',
                click: NOOP_CLICK_HANDLER,
              }
            ]
          },
          {
            label: 'CRM Platforms',
            type: 'group',
            children: [
              {
                label: 'Salesforce CRM',
                click: NOOP_CLICK_HANDLER,
              },
              {
                label: 'Microsoft Dynamics CRM',
                click: NOOP_CLICK_HANDLER,
              },
              {
                label: 'Oracle CX Cloud',
                click: NOOP_CLICK_HANDLER,
              },
              {
                label: 'HubSpot CRM',
                click: NOOP_CLICK_HANDLER,
              }
            ]
          },
          {
            label: 'HCM Solutions',
            type: 'group',
            children: [
              {
                label: 'Workday HCM',
                click: NOOP_CLICK_HANDLER,
              },
              {
                label: 'SAP SuccessFactors',
                click: NOOP_CLICK_HANDLER,
              },
              {
                label: 'Oracle HCM Cloud',
                click: NOOP_CLICK_HANDLER,
              },
              {
                label: 'ADP Workforce',
                click: NOOP_CLICK_HANDLER,
              }
            ]
          },
          {
            label: 'PLM Platforms',
            type: 'group',
            children: [
              {
                label: 'Siemens Teamcenter',
                click: NOOP_CLICK_HANDLER,
              },
              {
                label: 'PTC Windchill',
                click: NOOP_CLICK_HANDLER,
              },
              {
                label: 'Autodesk Fusion',
                click: NOOP_CLICK_HANDLER,
              },
              {
                label: 'Dassault 3DEXPERIENCE',
                click: NOOP_CLICK_HANDLER,
              }
            ]
          }
        ]
      },
      {
        label: 'Talent',
        type: 'section',
        children: [
          {
            label: 'Hire EA Experts',
            click: NOOP_CLICK_HANDLER,
          },
          {
            label: 'Find Projects',
            click: NOOP_CLICK_HANDLER,
          },
          {
            label: 'Join as Experts',
            click: NOOP_CLICK_HANDLER,
          }
        ]
      },
      {
        label: 'About',
        click: NOOP_CLICK_HANDLER,
      },
      {
        label: 'Contact Us',
        route: '/contact-us'
      },
    ] satisfies NavItem[];

    return {
      outer,
      inner
    };
  })();

  protected readonly inner = (() => {
    const byNavigation = {
      source: signal<NavItem | null>(null),
      items: signal<NavItem[]>([]),
      compute: () => byNavigation.source() && byNavigation.items() || []
    } as const;

    const opened = signal(false);

    const items = linkedSignal(() => byNavigation.compute());

    return {
      byNavigation,
      items,
      opened
    } as const;
  })();

  private updateSidenav() {
    const state = this.service.state;

    effect(() => {
      this.service.navigating$();
      untracked(() => {
        if (state.mode$() === 'over')
          this.outer.close();
      });
    }, { injector: this.injector });

    effect(() => {
      const opened = state.opened$();
      this.outer.opened = opened;
    }, { injector: this.injector });

    effect(() => {
      const mode = state.mode$();
      this.outer.mode = mode;
    }, { injector: this.injector });

    this.outer.openedChange
      .pipe(takeUntilDestroyed(this.dRef))
      .subscribe((open) => {
        if (!open) {
          const items = this.inner.byNavigation.compute();
          this.inner.items.set(items);
          if (!items.length)
            this.inner.opened.set(false);
        }
      });
  }

  private activateInnerMenu(source: NavItem, inner: () => NavItem[] | Promise<NavItem[]>) {
    return async (active: boolean) => {
      const items = await inner();
      if (active) {
        this.inner.byNavigation.source.set(source);
        this.inner.byNavigation.items.set(items);
        this.inner.opened.set(true);
      }
      else {
        this.inner.byNavigation.source.update(old => old === source ? null : old);
        if (!this.inner.items().length)
          this.inner.opened.set(false);
      }

    };
  }

  private openInnerMenu(inner: () => NavItem[] | Promise<NavItem[]>) {
    return async () => {
      const items = await inner();
      this.inner.items.set(items);
      this.inner.opened.set(true);
    };
  }

  ngOnInit(): void {
    this.updateSidenav();
  }
}
